import * as React from "react";
import { log10, round } from "mathjs";
import { values } from "lodash";
import { Plan2PlateDrawer, Plan2PlateDrawerHeader } from "../Plan2PlateDrawer";
import { Button } from "../ui/Button";
import { Switch } from "../ui/Toggle";
import { Label } from "../ui/Label";
import { NumberInput } from "../ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/Select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/Popover";
import {
  ActivityLevelEnum,
  activityLevels,
  FormulaEnum,
  formulas,
  GenderEnum,
  genders,
  GoalEnum,
  goals,
} from "../../data/bmrComputation";

export interface BmrCalculatorSheetProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (bmr: number) => void;
}

export const BmrCalculatorSheet = ({
  open,
  onClose,
  onSubmit,
}: BmrCalculatorSheetProps) => {
  const [goal, setGoal] = React.useState<GoalEnum>(GoalEnum.Lose);
  const [formula, setFormula] = React.useState<FormulaEnum>(FormulaEnum.Oxford);
  const [useMetric, setUseMetric] = React.useState(true);

  const [gender, setGender] = React.useState<GenderEnum>(GenderEnum.Diverse);
  const [age, setAge] = React.useState<number | undefined>();
  const [height, setHeight] = React.useState<number | undefined>();
  const [weight, setWeight] = React.useState<number | undefined>();

  const [bodyFatPercentage, setBodyFatPercentage] = React.useState<
    number | undefined
  >();
  const [bodyFatPercentageUnknown, setBodyFatPercentageUnknown] =
    React.useState(false);
  const [waist, setWaist] = React.useState<number | undefined>();
  const [neck, setNeck] = React.useState<number | undefined>();
  const [hip, setHip] = React.useState<number | undefined>();

  const [activityLevel, setActivityLevel] = React.useState<ActivityLevelEnum>(
    ActivityLevelEnum.Moderate,
  );

  const formulasWithBodyFat = [FormulaEnum.Cunningham, FormulaEnum.KatchMcArdle];

  const canBodyFatBeComputed =
    bodyFatPercentage ||
    (waist && neck && (gender == GenderEnum.Male || hip));

  const canBeComputed =
    !!age &&
    !!height &&
    !!weight &&
    (!formulasWithBodyFat.includes(formula) || !!canBodyFatBeComputed);

  const computedBmr = React.useMemo(() => {
    if (!canBeComputed) return undefined;
    const weightInKg = useMetric ? weight! : weight! * 0.453592;
    const heightInCm = useMetric ? height! : height! * 30.48;

    let bfp = bodyFatPercentage;
    if (!bfp && formulasWithBodyFat.includes(formula)) {
      if (gender == GenderEnum.Male) {
        bfp = 86.01 * log10(waist! - neck!) - 70.041 * log10(height!) + 36.76;
      } else {
        bfp =
          163.205 * log10(waist! + hip! - neck!) -
          97.684 * log10(height!) -
          78.387;
      }
    }

    switch (formula) {
      case FormulaEnum.Cunningham: {
        const lean = weightInKg * (1 - bfp! / 100);
        return 500 + 22 * lean;
      }
      case FormulaEnum.KatchMcArdle: {
        const lean = weightInKg * (1 - bfp! / 100);
        return 370 + 21.6 * lean;
      }
      case FormulaEnum.HarrisBenedict:
        return gender === GenderEnum.Male
          ? 88.362 + 13.397 * weightInKg + 4.799 * heightInCm - 5.677 * age!
          : 447.593 + 9.247 * weightInKg + 3.098 * heightInCm - 4.33 * age!;
      case FormulaEnum.MifflinStJeor:
        return gender === GenderEnum.Male
          ? 9.99 * weightInKg + 6.25 * heightInCm - 4.92 * age! + 5
          : 9.99 * weightInKg + 6.25 * heightInCm - 4.92 * age! - 161;
      case FormulaEnum.Schofield:
        return gender === GenderEnum.Male
          ? 88.362 + 13.397 * weightInKg + 4.799 * heightInCm - 5.677 * age!
          : 447.593 + 9.247 * weightInKg + 3.098 * heightInCm - 4.33 * age!;
      case FormulaEnum.Oxford:
        return oxford(gender, weightInKg, heightInCm, age!);
      default:
        return undefined;
    }
  }, [
    canBeComputed,
    useMetric,
    weight,
    height,
    bodyFatPercentage,
    gender,
    waist,
    neck,
    hip,
    formula,
    age,
  ]);

  const withActivity =
    computedBmr != null
      ? computedBmr *
        (gender === GenderEnum.Male
          ? activityLevels[activityLevel].maleFactor
          : activityLevels[activityLevel].defaultFactor)
      : undefined;

  const goalFactor =
    goal === GoalEnum.Maintain ? 0.85 : goal === GoalEnum.Lose ? 0.75 : 1.2;
  const withGoal = withActivity != null ? withActivity * goalFactor : undefined;

  return (
    <Plan2PlateDrawer
      open={open}
      onClose={onClose}
      header={<Plan2PlateDrawerHeader heading="Bedarf-Rechner" />}
      body={
        <div className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label>BMR-Formel</Label>
              <Select
                value={String(formula)}
                onValueChange={(v) => setFormula(Number(v) as FormulaEnum)}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue>{formulas[formula].description}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {values(formulas).map((f) => (
                    <SelectItem key={f.id} value={String(f.id)}>
                      {f.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FormulaInfoPopover />
          </div>

          <div className="flex items-center justify-between rounded-card border border-line bg-surface-card px-3 py-2">
            <span className="text-xs text-ink">Metrisches System</span>
            <Switch
              checked={useMetric}
              onCheckedChange={setUseMetric}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Geschlecht">
              <Select
                value={String(gender)}
                onValueChange={(v) => setGender(Number(v) as GenderEnum)}
              >
                <SelectTrigger>
                  <SelectValue>{genders[gender].description}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {values(genders).map((g) => (
                    <SelectItem key={g.id} value={String(g.id)}>
                      {g.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Alter">
              <NumberInput value={age} onValueChange={setAge} />
            </Field>
            <Field label={"Größe " + (useMetric ? "(cm)" : "(ft)")}>
              <NumberInput value={height} onValueChange={setHeight} />
            </Field>
            <Field label={"Gewicht " + (useMetric ? "(kg)" : "(lbs)")}>
              <NumberInput value={weight} onValueChange={setWeight} />
            </Field>
          </div>

          {formulasWithBodyFat.includes(formula) && (
            <div className="space-y-3 rounded-card border border-line bg-surface p-3">
              <Field label="Körperfettanteil (%)">
                <div className="flex items-center gap-2">
                  <NumberInput
                    value={bodyFatPercentage}
                    onValueChange={setBodyFatPercentage}
                    disabled={bodyFatPercentageUnknown}
                  />
                  <label className="flex shrink-0 items-center gap-1 text-xs text-ink-muted">
                    <input
                      type="checkbox"
                      className="accent-teal"
                      checked={bodyFatPercentageUnknown}
                      onChange={(e) => {
                        setBodyFatPercentageUnknown(e.target.checked);
                        if (e.target.checked) setBodyFatPercentage(undefined);
                        else {
                          setWaist(undefined);
                          setNeck(undefined);
                          setHip(undefined);
                        }
                      }}
                    />
                    unbekannt
                  </label>
                </div>
              </Field>
              {bodyFatPercentageUnknown && (
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label={
                      (gender == GenderEnum.Male
                        ? "Bauchumfang "
                        : "Taillenumfang ") + (useMetric ? "(cm)" : "(ft)")
                    }
                  >
                    <NumberInput value={waist} onValueChange={setWaist} />
                  </Field>
                  <Field
                    label={"Halsumfang " + (useMetric ? "(cm)" : "(ft)")}
                  >
                    <NumberInput value={neck} onValueChange={setNeck} />
                  </Field>
                  {gender !== GenderEnum.Male && (
                    <Field
                      label={"Hüftumfang " + (useMetric ? "(cm)" : "(ft)")}
                    >
                      <NumberInput value={hip} onValueChange={setHip} />
                    </Field>
                  )}
                </div>
              )}
            </div>
          )}

          <Field label="Aktivitätslevel">
            <Select
              value={String(activityLevel)}
              onValueChange={(v) =>
                setActivityLevel(Number(v) as ActivityLevelEnum)
              }
            >
              <SelectTrigger>
                <SelectValue>
                  {activityLevels[activityLevel].description}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {values(activityLevels).map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Ziel">
            <Select
              value={String(goal)}
              onValueChange={(v) => setGoal(Number(v) as GoalEnum)}
            >
              <SelectTrigger>
                <SelectValue>{goals[goal].description}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {values(goals).map((g) => (
                  <SelectItem key={g.id} value={String(g.id)}>
                    {g.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="space-y-1 border-t border-line pt-3 text-xs">
            {computedBmr != null && (
              <div className="flex justify-between">
                <span className="text-ink-muted">Grundumsatz</span>
                <span className="font-bold">
                  {round(computedBmr, 0)} kcal
                </span>
              </div>
            )}
            {withActivity != null && (
              <div className="flex justify-between">
                <span className="text-ink-muted">Energieverbrauch</span>
                <span className="font-bold">
                  {round(withActivity, 0)} kcal
                </span>
              </div>
            )}
            {withGoal != null && (
              <div className="flex justify-between text-gold">
                <span>Empfohlener Bedarf</span>
                <span className="font-bold">{round(withGoal, 0)} kcal</span>
              </div>
            )}
          </div>
        </div>
      }
      footer={
        <Button
          fullWidth
          disabled={!canBeComputed || withGoal == null}
          onClick={() => {
            if (withGoal != null) onSubmit(withGoal);
            onClose();
          }}
        >
          Übernehmen
        </Button>
      }
    />
  );
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function FormulaInfoPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="BMR-Formel Info"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-card border border-line bg-surface-card text-ink-muted"
        >
          ?
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="space-y-2">
          {values(formulas).map((f) => (
            <div key={f.id}>
              <div className="text-xs font-bold">{f.description}</div>
              <div className="text-xs text-ink-muted">{f.text}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function oxford(
  gender: GenderEnum,
  weightInKg: number,
  heightInCm: number,
  age: number,
): number {
  if (gender === GenderEnum.Male) {
    if (age <= 3) return 28.2 * weightInKg + (859 * heightInCm) / 100 - 371;
    if (age <= 10) return 15.1 * weightInKg + (313 * heightInCm) / 100 + 306;
    if (age <= 18) return 15.6 * weightInKg + (266 * heightInCm) / 100 + 299;
    if (age <= 30) return 14.4 * weightInKg + (313 * heightInCm) / 100 + 113;
    if (age <= 60) return 11.4 * weightInKg + (541 * heightInCm) / 100 - 137;
    return 11.4 * weightInKg + (541 * heightInCm) / 100 - 256;
  } else {
    if (age <= 3) return 30.4 * weightInKg + (703 * heightInCm) / 100 - 287;
    if (age <= 10) return 15.9 * weightInKg + (210 * heightInCm) / 100 + 349;
    if (age <= 18) return 9.4 * weightInKg + (249 * heightInCm) / 100 + 462;
    if (age <= 30) return 10.4 * weightInKg + (615 * heightInCm) / 100 - 282;
    if (age <= 60) return 8.18 * weightInKg + (502 * heightInCm) / 100 - 11.6;
    return 11.4 * weightInKg + (541 * heightInCm) / 100 - 256;
  }
}
